import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { NavigateOptions } from 'react-router-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'lodash-es';

type SearchParamValue = boolean | string | number | readonly string[];

interface SearchParamsControl {
    searchParams: URLSearchParams;
    updateSearchParams: (
        key: string,
        value: SearchParamValue,
        navigateOptions?: NavigateOptions
    ) => void;
    updateSearchParamsBatch: (
        updates: Readonly<Record<string, SearchParamValue>>,
        navigateOptions?: NavigateOptions
    ) => void;
}

const SearchParamsContext = createContext<SearchParamsControl | null>(null);

function applySearchParam(params: URLSearchParams, key: string, value: SearchParamValue): void {
    if (
        (key === 'page' && value === 1) ||
        (key !== 'page' && typeof value !== 'number' && isEmpty(value))
    ) {
        params.delete(key);
    } else {
        params.set(key, String(value));
    }
}

export function SearchParamsProvider({ children }: { children: ReactNode }): JSX.Element {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const pendingRef = useRef({ location, params: searchParams });

    const updateSearchParamsBatch = useCallback(
        (
            updates: Readonly<Record<string, SearchParamValue>>,
            navigateOptions?: NavigateOptions
        ): void => {
            // Synchronize only when called, so an uncommitted render cannot change pending state.
            if (pendingRef.current.location !== location) {
                pendingRef.current = { location, params: searchParams };
            }
            const currentParams = pendingRef.current.params;
            const newParams = new URLSearchParams(currentParams);
            Object.entries(updates).forEach(([key, value]) => {
                applySearchParam(newParams, key, value);
            });

            if (newParams.toString() === currentParams.toString()) {
                return;
            }

            pendingRef.current.params = newParams;
            navigate(
                { pathname: location.pathname, search: newParams.toString() },
                navigateOptions
            );
        },
        [location, navigate, searchParams]
    );

    const updateSearchParams = useCallback(
        (key: string, value: SearchParamValue, navigateOptions?: NavigateOptions): void => {
            updateSearchParamsBatch({ [key]: value }, navigateOptions);
        },
        [updateSearchParamsBatch]
    );

    const control = useMemo(
        () => ({ searchParams, updateSearchParams, updateSearchParamsBatch }),
        [searchParams, updateSearchParams, updateSearchParamsBatch]
    );

    return <SearchParamsContext.Provider value={control}>{children}</SearchParamsContext.Provider>;
}

export function useUpdateSearchParams(): SearchParamsControl {
    const control = useContext(SearchParamsContext);
    if (control === null) {
        throw new Error('useUpdateSearchParams requires SearchParamsProvider.');
    }
    return control;
}
